/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useMemo } from 'react';
import { createLinkControl } from '@veeam/components';
import { capitalize } from '@veeam-vspc/shared/helpers';
import { colors as palette, useGlobalLang, useGlobalServices } from '@veeam-vspc/shared/components';
import { observer } from 'mobx-react-lite';
import { ManagedVmType } from '@veeam-vspc/models/web-controllers';
import { useLocation } from 'react-router-dom';

import type { VspcLang } from 'configs/languages';

import { BaseTrendChart } from '../../../BaseTrendChart';
import { ManagedVmsContext } from './contexts/ManagedVmsContext';
import { ManagedVmsService } from './services/managed-vms.service';
import { ManagedVmsStore } from './stores/ManagedVmsStore';
import { StatefulCombobox } from 'views/components/StatefulCombobox';
import { useManagedVmsStore } from './hooks/use-managed-vms-store';
import { useLocalStorageValue } from 'views/hooks';

import type { ComboboxItem } from 'core/interfaces';

export const ChartLayout = observer(() => {
    const lang = useGlobalLang<VspcLang>();
    const managedVmStore = useManagedVmsStore();
    const { transportService } = useGlobalServices();
    const managedVmsService = useMemo(() => new ManagedVmsService(transportService, lang), [transportService, lang]);

    const params = {
        colors: [palette.B400],
        yTitle: lang.VMS,
    };

    const title = {
        text: capitalize(lang.MANAGED_VMS),
        tools: (
            <StatefulCombobox
                controlRenderer={createLinkControl()}
                onChange={managedVmStore.setManagedVmType}
                stateId={managedVmStore.lsStateId}
                textGetter={(p: ComboboxItem) => p.text}
                value={managedVmStore.managedVmType}
                valueGetter={(p: ComboboxItem) => p.id}
                data={[
                    { id: ManagedVmType.All, text: lang.ALL },
                    { id: ManagedVmType.VSphere, text: lang.VSPHERE },
                    { id: ManagedVmType.HyperV, text: lang.HYPER_V },
                    { id: ManagedVmType.Ahv, text: lang.AHV },
                ]}
            />
        ),
    };

    return (
        <BaseTrendChart
            objectsType={managedVmStore.managedVmType}
            params={params}
            service={managedVmsService}
            title={title}
        />
    );
});

export const ManagedVms: React.FC = () => {
    const { pathname } = useLocation();
    const stateId = `${pathname}.ManagedVms.ManagedVmType`;
    const initialType = useLocalStorageValue<ManagedVmType>(stateId, ManagedVmType.All);

    const managedVmsStore = useMemo(() => new ManagedVmsStore(initialType, stateId), [initialType, stateId]);

    return (
        <ManagedVmsContext.Provider value={managedVmsStore}>
            <ChartLayout />
        </ManagedVmsContext.Provider>
    );
};
